// src/app/admindashobard/components/AdminSidebar.jsx

import * as React from "react";
import {
  Box,
  Collapse,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";

import SpaceDashboardRoundedIcon from "@mui/icons-material/SpaceDashboardRounded";
import HistoryEduRoundedIcon from "@mui/icons-material/HistoryEduRounded";
import ApartmentRoundedIcon from "@mui/icons-material/ApartmentRounded";
import Groups2RoundedIcon from "@mui/icons-material/Groups2Rounded";
import DevicesRoundedIcon from "@mui/icons-material/DevicesRounded";
import MonitorHeartRoundedIcon from "@mui/icons-material/MonitorHeartRounded";
import ShowChartRoundedIcon from "@mui/icons-material/ShowChartRounded";
import MedicationOutlinedIcon from "@mui/icons-material/MedicationOutlined";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded";
import WaterDropRoundedIcon from "@mui/icons-material/WaterDropRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";

import { Colors } from "../../utils/Colors";
import { ROUTES } from "../../shared/constants/routes";

const drawerWidth = 290;

function SidebarItem({
  icon,
  text,
  selected = false,
  onClick,
  endIcon = null,
  nested = false,
}) {
  return (
    <ListItemButton
      onClick={onClick}
      sx={{
        mx: nested ? 3 : 2,
        mb: 1,
        minHeight: 48,
        pl: nested ? 4 : 2,
        borderRadius: 2.5,
        bgcolor: selected ? Colors.brand.primary : "transparent",
        color: selected ? "#ffffff" : Colors.text.header,
        "&:hover": {
          bgcolor: selected ? Colors.brand.primaryDark : Colors.bg.hover,
        },
      }}
    >
      <ListItemIcon
        sx={{
          minWidth: nested ? 30 : 40,
          color: selected ? "#ffffff" : Colors.text.icon,
        }}
      >
        {icon}
      </ListItemIcon>

      <ListItemText
        primary={text}
        primaryTypographyProps={{
          fontSize: nested ? 15 : 16,
          fontWeight: selected ? 700 : 600,
        }}
      />

      {endIcon}
    </ListItemButton>
  );
}

export default function AdminSidebar({ open, onClose }) {
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;

  const isActive = (path) => pathname === path;
  const isStartsWith = (path) => pathname.startsWith(path);

  const [deviceOpen, setDeviceOpen] = React.useState(
    pathname.startsWith("/device")
  );
  const [monitoringOpen, setMonitoringOpen] = React.useState(
    pathname.startsWith("/monitoring")
  );
  const [vitalsOpen, setVitalsOpen] = React.useState(
    pathname.startsWith("/vitals")
  );
  const [medicineOpen, setMedicineOpen] = React.useState(
    pathname.startsWith("/medicine")
  );
  const [balanceOpen, setBalanceOpen] = React.useState(
    pathname.startsWith("/balance")
  );

  React.useEffect(() => {
    if (pathname.startsWith("/device")) setDeviceOpen(true);
    if (pathname.startsWith("/monitoring")) setMonitoringOpen(true);
    if (pathname.startsWith("/vitals")) setVitalsOpen(true);
    if (pathname.startsWith("/medicine")) setMedicineOpen(true);
    if (pathname.startsWith("/balance")) setBalanceOpen(true);
  }, [pathname]);

  const handleNavigate = (path) => {
    navigate(path);
    if (onClose) onClose();
  };

  const drawerContent = (
    <Box
      sx={{
        height: "100%",
        bgcolor: Colors.bg.paper,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: 2,
              bgcolor: Colors.brand.primary,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              fontWeight: 800,
              fontSize: 28,
              boxShadow: Colors.shadow.brand,
            }}
          >
            F
          </Box>

          <Typography
            sx={{
              fontSize: 22,
              fontWeight: 800,
              color: Colors.brand.primary,
            }}
          >
            Fludee
          </Typography>
        </Stack>
      </Box>

      <Divider sx={{ mb: 1 }} />

      <List sx={{ pt: 1, pb: 2, overflowY: "auto" }}>
        <SidebarItem
          icon={<SpaceDashboardRoundedIcon />}
          text="Fludee Dashboard"
          selected={isActive(ROUTES.ADMIN_DASHBOARD)}
          onClick={() => handleNavigate(ROUTES.ADMIN_DASHBOARD)}
        />

        <SidebarItem
          icon={<HistoryEduRoundedIcon />}
          text="Hospital Form"
          selected={isActive(ROUTES.CORE_HOSPITAL)}
          onClick={() => handleNavigate(ROUTES.CORE_HOSPITAL)}
        />

        <SidebarItem
          icon={<ApartmentRoundedIcon />}
          text="Organization Profile"
          selected={isActive(ROUTES.ORG_PROFILE)}
          onClick={() => handleNavigate(ROUTES.ORG_PROFILE)}
        />

        <SidebarItem
          icon={<Groups2RoundedIcon />}
          text="Patients Form"
          selected={isActive(ROUTES.PATIENTS_CREATE)}
          onClick={() => handleNavigate(ROUTES.PATIENTS_CREATE)}
        />

        <SidebarItem
          icon={<Groups2RoundedIcon />}
          text="Admission Form"
          selected={isActive(ROUTES.ADMISSIONS_CREATE)}
          onClick={() => handleNavigate(ROUTES.ADMISSIONS_CREATE)}
        />

        <SidebarItem
          icon={<Groups2RoundedIcon />}
          text="Patients-Profile"
          selected={isStartsWith("/patients/profile")}
          onClick={() => handleNavigate(ROUTES.PATIENTS_LIST)}
        />

        <SidebarItem
          icon={<DevicesRoundedIcon />}
          text="Fludee Device"
          selected={isStartsWith("/device")}
          onClick={() => setDeviceOpen((prev) => !prev)}
          endIcon={
            deviceOpen ? (
              <KeyboardArrowDownRoundedIcon sx={{ color: Colors.text.icon }} />
            ) : (
              <KeyboardArrowRightRoundedIcon sx={{ color: Colors.text.icon }} />
            )
          }
        />

        <Collapse in={deviceOpen} timeout="auto" unmountOnExit>
          <List disablePadding>
            <SidebarItem
              nested
              icon={<Box sx={{ width: 8 }} />}
              text="Device List"
              selected={isActive(ROUTES.DEVICE_LIST)}
              onClick={() => handleNavigate(ROUTES.DEVICE_LIST)}
            />
            <SidebarItem
              nested
              icon={<Box sx={{ width: 8 }} />}
              text="Create Device"
              selected={isActive(ROUTES.DEVICE_CREATE)}
              onClick={() => handleNavigate(ROUTES.DEVICE_CREATE)}
            />
            <SidebarItem
              nested
              icon={<Box sx={{ width: 8 }} />}
              text="Assignments"
              selected={isActive(ROUTES.DEVICE_ASSIGNMENT_LIST)}
              onClick={() => handleNavigate(ROUTES.DEVICE_ASSIGNMENT_LIST)}
            />
            <SidebarItem
              nested
              icon={<Box sx={{ width: 8 }} />}
              text="Assign Device"
              selected={isActive(ROUTES.DEVICE_ASSIGNMENT_CREATE)}
              onClick={() => handleNavigate(ROUTES.DEVICE_ASSIGNMENT_CREATE)}
            />
            <SidebarItem
              nested
              icon={<Box sx={{ width: 8 }} />}
              text="Sensor Channels"
              selected={isActive(ROUTES.SENSOR_CHANNELS_LIST)}
              onClick={() => handleNavigate(ROUTES.SENSOR_CHANNELS_LIST)}
            />
          </List>
        </Collapse>

        <SidebarItem
          icon={<MonitorHeartRoundedIcon />}
          text="Monitoring"
          selected={isStartsWith("/monitoring")}
          onClick={() => setMonitoringOpen((prev) => !prev)}
          endIcon={
            monitoringOpen ? (
              <KeyboardArrowDownRoundedIcon sx={{ color: Colors.text.icon }} />
            ) : (
              <KeyboardArrowRightRoundedIcon sx={{ color: Colors.text.icon }} />
            )
          }
        />

        <Collapse in={monitoringOpen} timeout="auto" unmountOnExit>
          <List disablePadding>
            <SidebarItem
              nested
              icon={<Box sx={{ width: 8 }} />}
              text="Live Monitoring"
              selected={isActive(ROUTES.MONITORING_LIVE)}
              onClick={() => handleNavigate(ROUTES.MONITORING_LIVE)}
            />
            <SidebarItem
              nested
              icon={<Box sx={{ width: 8 }} />}
              text="Sessions"
              selected={isActive(ROUTES.MONITORING_SESSIONS)}
              onClick={() => handleNavigate(ROUTES.MONITORING_SESSIONS)}
            />
            <SidebarItem
              nested
              icon={<Box sx={{ width: 8 }} />}
              text="Alarms"
              selected={isActive(ROUTES.MONITORING_ALARMS)}
              onClick={() => handleNavigate(ROUTES.MONITORING_ALARMS)}
            />
            <SidebarItem
              nested
              icon={<Box sx={{ width: 8 }} />}
              text="History"
              selected={isActive(ROUTES.MONITORING_HISTORY)}
              onClick={() => handleNavigate(ROUTES.MONITORING_HISTORY)}
            />
            <SidebarItem
              nested
              icon={<Box sx={{ width: 8 }} />}
              text="Drip Sessions"
              selected={isActive(ROUTES.drip_sessions)}
              onClick={() => handleNavigate(ROUTES.drip_sessions)}
            />
            <SidebarItem
              nested
              icon={<Box sx={{ width: 8 }} />}
              text="Urine Output"
              selected={isActive(ROUTES.Urine_output)}
              onClick={() => handleNavigate(ROUTES.Urine_output)}
            />
            <SidebarItem
              nested
              icon={<Box sx={{ width: 8 }} />}
              text="Live Page"
              selected={isActive(ROUTES.Livemonitoringpages)}
              onClick={() => handleNavigate(ROUTES.Livemonitoringpages)}
            />
          </List>
        </Collapse>

        <SidebarItem
          icon={<ShowChartRoundedIcon />}
          text="Vitals"
          selected={isStartsWith("/vitals")}
          onClick={() => setVitalsOpen((prev) => !prev)}
          endIcon={
            vitalsOpen ? (
              <KeyboardArrowDownRoundedIcon sx={{ color: Colors.text.icon }} />
            ) : (
              <KeyboardArrowRightRoundedIcon sx={{ color: Colors.text.icon }} />
            )
          }
        />

        <Collapse in={vitalsOpen} timeout="auto" unmountOnExit>
          <List disablePadding>
            <SidebarItem
              nested
              icon={<Box sx={{ width: 8 }} />}
              text="Streaming"
              selected={isActive(ROUTES.VITALS_STREAMING)}
              onClick={() => handleNavigate(ROUTES.VITALS_STREAMING)}
            />
            <SidebarItem
              nested
              icon={<Box sx={{ width: 8 }} />}
              text="Rules"
              selected={isActive(ROUTES.VITALS_RULES)}
              onClick={() => handleNavigate(ROUTES.VITALS_RULES)}
            />
            <SidebarItem
              nested
              icon={<Box sx={{ width: 8 }} />}
              text="History"
              selected={isActive(ROUTES.VITALS_HISTORY)}
              onClick={() => handleNavigate(ROUTES.VITALS_HISTORY)}
            />
          </List>
        </Collapse>

        <SidebarItem
          icon={<WaterDropRoundedIcon />}
          text="Fluid Balance"
          selected={isStartsWith("/balance") || isActive(ROUTES.FluidTherapy)}
          onClick={() => setBalanceOpen((prev) => !prev)}
          endIcon={
            balanceOpen ? (
              <KeyboardArrowDownRoundedIcon sx={{ color: Colors.text.icon }} />
            ) : (
              <KeyboardArrowRightRoundedIcon sx={{ color: Colors.text.icon }} />
            )
          }
        />

        <Collapse in={balanceOpen} timeout="auto" unmountOnExit>
          <List disablePadding>
            <SidebarItem
              nested
              icon={<Box sx={{ width: 8 }} />}
              text="Fludee Balance"
              selected={isActive(ROUTES.FLUDEE_BALANCE)}
              onClick={() => handleNavigate(ROUTES.FLUDEE_BALANCE)}
            />
            <SidebarItem
              nested
              icon={<Box sx={{ width: 8 }} />}
              text="Intake Output"
              selected={isActive(ROUTES.INTAKE_OUTPUT)}
              onClick={() => handleNavigate(ROUTES.INTAKE_OUTPUT)}
            />
            <SidebarItem
              nested
              icon={<Box sx={{ width: 8 }} />}
              text="Fluid Therapy"
              selected={isActive(ROUTES.FluidTherapy)}
              onClick={() => handleNavigate(ROUTES.FluidTherapy)}
            />
          </List>
        </Collapse>

        <SidebarItem
          icon={<MedicationOutlinedIcon />}
          text="Medicine"
          selected={isStartsWith("/medicine")}
          onClick={() => setMedicineOpen((prev) => !prev)}
          endIcon={
            medicineOpen ? (
              <KeyboardArrowDownRoundedIcon sx={{ color: Colors.text.icon }} />
            ) : (
              <KeyboardArrowRightRoundedIcon sx={{ color: Colors.text.icon }} />
            )
          }
        />

        <Collapse in={medicineOpen} timeout="auto" unmountOnExit>
          <List disablePadding>
            <SidebarItem
              nested
              icon={<Box sx={{ width: 8 }} />}
              text="Chart"
              selected={isActive(ROUTES.MEDICINE_CHART)}
              onClick={() => handleNavigate(ROUTES.MEDICINE_CHART)}
            />
            <SidebarItem
              nested
              icon={<Box sx={{ width: 8 }} />}
              text="Schedule"
              selected={isActive(ROUTES.MEDICINE_SCHEDULE)}
              onClick={() => handleNavigate(ROUTES.MEDICINE_SCHEDULE)}
            />
          </List>
        </Collapse>

        <SidebarItem
          icon={<SettingsRoundedIcon />}
          text="Settings"
          selected={isActive(ROUTES.SETTINGS)}
          onClick={() => handleNavigate(ROUTES.SETTINGS)}
        />
      </List>
    </Box>
  );

  return (
    <>
      <Drawer
        variant="temporary"
        open={open}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            borderRight: `1px solid ${Colors.border.light}`,
            boxSizing: "border-box",
          },
        }}
      >
        {drawerContent}
      </Drawer>

      <Drawer
        variant="permanent"
        open
        sx={{
          display: { xs: "none", md: "block" },
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            borderRight: `1px solid ${Colors.border.light}`,
            bgcolor: Colors.bg.paper,
          },
        }}
      >
        {drawerContent}
      </Drawer>
    </>
  );
}

export { drawerWidth };